/* Silver band: what the silver does, in one loop. Bacteria gather on the knit, a silver ring spreads from the yarn and
   each one shrinks away as the ring reaches it, then the knit stays clear. It plays and pauses with the band's film
   (one control for both); with reduced motion, or before the film starts, it holds the moment the ring is half way. */
const CYCLE = 9000;
const RING_START = 0.34;
const RING_END = 0.66;
const HOLD = 0.5;

class LurenSilverEffect extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const microbes = [...this.querySelectorAll('[data-microbe]')];
    const ring = this.querySelector('[data-ring]');
    const steps = [...this.querySelectorAll('[data-step]')];
    if (!microbes.length || !ring || !this.animate) return;

    const svg = this.querySelector('svg').viewBox.baseVal;
    const cx = svg.width / 2;
    const cy = svg.height / 2;
    const reach = Math.hypot(cx, cy);
    const timing = { duration: CYCLE, iterations: Infinity, fill: 'both' };

    this.animations = microbes.map((node, index) => {
      const x = Number(node.dataset.x);
      const y = Number(node.dataset.y);
      const born = 0.02 + (index / microbes.length) * 0.18;
      const gone = RING_START + (RING_END - RING_START) * (Math.hypot(x - cx, y - cy) / reach);
      return node.animate([
        { offset: 0, opacity: 0, transform: 'scale(.2)' },
        { offset: born, opacity: 0, transform: 'scale(.2)' },
        { offset: born + 0.05, opacity: 1, transform: 'scale(1)' },
        { offset: born + 0.12, opacity: 1, transform: 'scale(.94)' },
        { offset: gone - 0.02, opacity: 1, transform: 'scale(1.04)' },
        { offset: gone + 0.05, opacity: 0, transform: 'scale(.15)' },
        { offset: 1, opacity: 0, transform: 'scale(.15)' }
      ], timing);
    });
    this.animations.push(ring.animate([
      { offset: 0, opacity: 0, transform: 'scale(.02)' },
      { offset: RING_START, opacity: 0, transform: 'scale(.02)' },
      { offset: RING_START + 0.03, opacity: 1, transform: 'scale(.12)' },
      { offset: RING_END, opacity: 0.85, transform: 'scale(1)' },
      { offset: RING_END + 0.08, opacity: 0, transform: 'scale(1.08)' },
      { offset: 1, opacity: 0, transform: 'scale(1.08)' }
    ], timing));
    const windows = [[0.02, RING_START], [RING_START, RING_END + 0.04], [RING_END + 0.04, 0.96]];
    steps.forEach((step, index) => {
      const [from, to] = windows[index] || windows[0];
      this.animations.push(step.animate([
        { offset: 0, opacity: 0 },
        { offset: from, opacity: 0 },
        { offset: Math.min(from + 0.03, to), opacity: 1 },
        { offset: Math.max(to - 0.03, from), opacity: 1 },
        { offset: to, opacity: 0 },
        { offset: 1, opacity: 0 }
      ], timing));
    });
    this.hold();

    const video = this.closest('luren-hero-video')?.querySelector('video');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    if (!video || motion.matches) return;
    const options = { signal: this.abort.signal };
    video.addEventListener('playing', () => this.play(), options);
    video.addEventListener('pause', () => this.pause(), options);
    motion.addEventListener('change', () => { if (motion.matches) this.hold(); }, options);
    if (!video.paused) this.play();
  }

  play() {
    this.animations?.forEach(animation => animation.play());
  }

  pause() {
    this.animations?.forEach(animation => animation.pause());
  }

  hold() {
    this.animations?.forEach(animation => {
      animation.pause();
      animation.currentTime = CYCLE * HOLD;
    });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.animations?.forEach(animation => animation.cancel());
  }
}

if (!customElements.get('luren-silver-effect')) customElements.define('luren-silver-effect', LurenSilverEffect);
